import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { configureStore, createSlice } from "@reduxjs/toolkit";
import { Provider } from "react-redux";

// Validation Schema using Yup
let schema = yup.object().shape({
  email: yup.string().email("Email should be valid").required("Email is Required"),
  password: yup.string().required("Password is Required"),
});

// Redux Slice for Authentication
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    message: "",
  },
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.isError = false;
      state.message = "";
    },
    loginSuccess: (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.user = action.payload;
    },
    loginFailure: (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
    },
  },
});

// Redux Store
const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
  },
});

const { loginStart, loginSuccess, loginFailure } = authSlice.actions;

// Reusable Custom Input Component
const CustomInput = ({ type, label, id, name, onChng, onBlr, val }) => {
  return (
    <div className="mb-3">
      <label htmlFor={id} className="form-label">{label}</label>
      <input
        type={type}
        id={id}
        name={name}
        className="form-control"
        onChange={onChng}
        onBlur={onBlr}
        value={val}
      />
    </div>
  );
};

// Login Component
const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isError, isSuccess, isLoading, message } = useSelector((state) => state.auth);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: schema,
    onSubmit: async (values) => {
      dispatch(loginStart());

      try {
        const response = await fetch("https://backend.gulabidecor.com/api/auth/userAdmin", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });

        const data = await response.json();

        if (response.ok) {
          localStorage.setItem("adminToken", data.adminToken);
          dispatch(loginSuccess(data.admin));
          navigate("/admin/orders");
        } else {
          dispatch(loginFailure(data.message || "Login failed"));
        }
      } catch (error) {
        dispatch(loginFailure("Something went wrong. Please try again."));
      }
    },
  });

  useEffect(() => {
    if (isSuccess) {
      navigate("/admin/orders");
    }
  }, [isSuccess, navigate]);

  return (
    <div className="py-5" style={{ background: "#F4DFB9", minHeight: "100vh" }}>
      <div className="my-5 w-25 bg-white rounded-3 mx-auto p-4">
        <h3 className="text-center title">Login</h3>
        <p className="text-center">Login to your account to continue.</p>
        <div className="error text-center">{isError ? message || "Invalid credentials" : ""}</div>
        <form onSubmit={formik.handleSubmit}>
          <CustomInput
            type="text"
            label="Email Address"
            id="email"
            name="email"
            onChng={formik.handleChange("email")}
            onBlr={formik.handleBlur("email")}
            val={formik.values.email}
          />
          <div className="error mt-2">{formik.touched.email && formik.errors.email}</div>

          <CustomInput
            type="password"
            label="Password"
            id="pass"
            name="password"
            onChng={formik.handleChange("password")}
            onBlr={formik.handleBlur("password")}
            val={formik.values.password}
          />
          <div className="error mt-2">{formik.touched.password && formik.errors.password}</div>

         
          <button
            className="border-0 px-3 py-2 text-white fw-bold w-100 text-center text-decoration-none fs-5"
            style={{ background: "#6B3F4F" }}
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// App Component (Wraps Login with Redux Provider)
const App = () => {
  return (
    <Provider store={store}>
      <Login />
    </Provider>
  );
};

export default App;
